// src/components/AlbumDetailPage.tsx

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button.tsx';
import { 
  ArrowLeft, 
  Image as ImageIcon, 
  ChevronLeft, 
  ChevronRight, 
  X, 
  CalendarDays,
  Download // İndirme ikonu eklendi
} from 'lucide-react';

// --------------------
// Veri Tipleri
// --------------------
interface GalleryImage {
  id: number;
  image: string;
  caption?: string;
}

interface FolderDetail {
  id: number;
  name: string;
  visibility?: string;
  created_at?: string;
  images: GalleryImage[];
}

export function AlbumDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // State Management
  const [album, setAlbum] = useState<FolderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  
  // Lightbox State
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // --------------------
  // Albüm Fetch
  // --------------------
  useEffect(() => {
    const fetchAlbum = async () => {
      setLoading(true);
      setErrorMsg(null);

      try {
        const apiUrl = `https://sdal-project-backend.onrender.com/api/gallery/folders/${id}/`;
        const token = localStorage.getItem("access");
        const res = await axios.get(apiUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        setAlbum(res.data);
      } catch (err: any) {
        console.error('Albüm yüklenemedi:', err);
        if (err.response?.status === 404) {
          setErrorMsg('Albüm bulunamadı veya silinmiş.');
        } else if (err.response?.status === 403) {
          setErrorMsg('Bu albüm sadece yetkili kişilere özeldir. Lütfen giriş yapın.');
        } else {
          setErrorMsg('Bağlantı hatası. Lütfen daha sonra tekrar deneyin.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAlbum();
    }
  }, [id]);

  // --------------------
  // Lightbox Kontroller
  // --------------------
  const closeModal = () => setSelectedIndex(null);

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedIndex === null || !album) return;
    setSelectedIndex((selectedIndex - 1 + album.images.length) % album.images.length);
  };

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedIndex === null || !album) return;
    setSelectedIndex((selectedIndex + 1) % album.images.length);
  };

  // Resim İndirme Fonksiyonu
  const handleDownload = async (e: React.MouseEvent, imageUrl: string) => {
    e.stopPropagation();
    if (isDownloading) return;

    try {
      setIsDownloading(true);
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      
      // URL'den dosya adını çıkar veya varsayılan bir ad ver
      const filename = imageUrl.split('/').pop() || `fotograf-${Date.now()}.jpg`;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Resim indirilirken hata oluştu:", error);
      // Fallback: Eğer CORS hatası olursa resmi yeni sekmede aç
      window.open(imageUrl, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  // Klavye ok tuşları ile fotoğraflar arası geçiş
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedIndex === null) return;
      if (e.key === 'ArrowLeft') setSelectedIndex((prev) => (prev! - 1 + album!.images.length) % album!.images.length);
      if (e.key === 'ArrowRight') setSelectedIndex((prev) => (prev! + 1) % album!.images.length);
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, album]);

  const handleBack = () => navigate('/gallery');

  // --------------------
  // Render States
  // --------------------
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 space-y-4">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-[#A8322D] rounded-full animate-spin" />
        <p className="text-slate-500 font-medium animate-pulse">Albüm yükleniyor...</p>
      </div>
    );
  }

  if (errorMsg || !album) {
    return (
      <div className="container mx-auto px-4 py-20 max-w-2xl text-center">
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-3xl p-10">
          <ImageIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-800 mb-2">Hata Oluştu</h3>
          <p className="text-slate-500 mb-6">{errorMsg}</p>
          <Button onClick={handleBack} className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8">
            Galeriye Dön
          </Button>
        </div>
      </div>
    );
  }

  // --------------------
  // Main Render
  // --------------------
  return (
    <div className="container mx-auto px-4 pt-8 pb-24 max-w-7xl">
      
      {/* Header Section */}
      <div className="space-y-6 mb-10">
        <Button variant="ghost" onClick={handleBack} className="gap-2 pl-0 hover:bg-transparent text-slate-500 hover:text-[#A8322D] transition-colors -ml-2">
          <ArrowLeft className="h-4 w-4" /> Geri Dön
        </Button>
        
        <div>
          <h1 className="text-3xl md:text-3xl font-extrabold text-slate-900 mb-3">{album.name}</h1>
          <div className="flex items-center gap-4 text-sm text-slate-500 font-medium">
            {album.created_at && (
              <span className="flex items-center gap-1.5">
                <CalendarDays className="w-4 h-4" /> 
                {new Date(album.created_at).toLocaleDateString("tr-TR", { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Empty Photo State */}
      {album.images.length === 0 ? (
        <div className="text-center py-20 bg-slate-50 border border-dashed border-slate-200 rounded-3xl">
          <ImageIcon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Bu albümde henüz fotoğraf bulunmuyor.</p>
        </div>
      ) : (
        /* Photo Grid - aspect-[4/3] kullanılarak daha düzgün fotoğraf oranları sağlandı */
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {album.images.map((img, index) => (
            <div 
              key={img.id} 
              onClick={() => setSelectedIndex(index)}
              className="group relative aspect-[4/3] bg-slate-100 rounded-2xl overflow-hidden cursor-pointer shadow-sm hover:shadow-xl transition-all duration-500"
            >
              <img 
                src={img.image} 
                alt={`Photo ${index + 1}`} 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
            </div>
          ))}
        </div>
      )}

      {/* Tam Ekran Lightbox Modal */}
      {selectedIndex !== null && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-200" onClick={closeModal}>
          
          {/* Sağ Üst Kontroller (İndir ve Kapat) */}
          <div className="absolute top-6 right-6 flex items-center gap-3 z-50">
            <button 
              onClick={(e) => handleDownload(e, album.images[selectedIndex].image)}
              disabled={isDownloading}
              className="p-2 text-white/70 hover:text-white bg-black/50 hover:bg-black/80 rounded-full backdrop-blur-md transition-all disabled:opacity-50"
              title="İndir"
            >
              <Download className={`w-6 h-6 ${isDownloading ? 'animate-bounce' : ''}`} />
            </button>
            <button 
              onClick={closeModal}
              className="p-2 text-white/70 hover:text-white bg-black/50 hover:bg-black/80 rounded-full backdrop-blur-md transition-all"
              title="Kapat"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Geri Butonu */}
          <button 
            onClick={prevImage}
            className="absolute left-4 md:left-10 p-3 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full backdrop-blur-md transition-all z-50"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          {/* İleri Butonu */}
          <button 
            onClick={nextImage}
            className="absolute right-4 md:right-10 p-3 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full backdrop-blur-md transition-all z-50"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          {/* Aktif Fotoğraf - Boyutlandırma iyileştirildi */}
          <div className="relative w-full h-full flex flex-col items-center justify-center p-4 md:p-12" onClick={(e) => e.stopPropagation()}>
            <img 
              src={album.images[selectedIndex].image} 
              alt="Genişletilmiş Fotoğraf" 
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-md shadow-2xl animate-in zoom-in-95 duration-300"
            />
            {/* Fotoğraf Sırası */}
            <div className="absolute bottom-6 left-0 right-0 text-center">
              <span className="bg-black/60 text-white text-sm px-4 py-2 rounded-full backdrop-blur-md tracking-wide">
                {selectedIndex + 1} / {album.images.length}
              </span>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}